import { formatDate } from '../../utils/DateTimeUtils';
import { useRecordContext } from 'react-admin';
import moment from 'moment';

//Common Styles
import {
  HighlightValue,
  HorizontalContainer,
  HorizontalLine,
  InfoPill,
  SectionHeader,
  SectionTitle,
  ShowLabel,
  ShowValue,
  StatusBooleanBadge,
  ThreeColGrid,
  WorkspacePanel,
  WorkspaceSection,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";

//Icons
import {
  CalendarIcon,
  ClipboardIcon,
  ClockIcon,
  ForkSpoonIcon,
  GlassIcon,
  PackageIcon,
  UserIcon,
  UsersIcon,
} from "../Svg";

//Colors
import { colors } from "../../styles/colors";

//Components
import CustomCallIconButton from "../Common/CallButton/CustomCallIconButton";

//Enums
import { UserType } from "../../enums/common";

//Icons
import { ChefCapIcon } from "../Svg/ChefCapIcon";
import { IconButton, Link, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

//Enums
import { ChefForPartyBookingTypeEnum } from "../../enums/ChefForPartyBookingTypeEnum";

const ChefForPartyOverview = () => {
  const record = useRecordContext();
  const foodPreferences = Array.isArray(record?.foodPreferences)
    ? record.foodPreferences.filter(Boolean).join(", ")
    : record?.foodPreferences &&
        typeof record.foodPreferences === "object"
      ? Object.values(record.foodPreferences).filter(Boolean).join(", ")
      : record?.foodPreferences || "-";

  const serviceAddonCounts = (record?.orders || []).reduce(
    (
      acc: { bartender: number; waiter: number },
      eachOrder: { orderItems?: any[] },
    ) => {
      (eachOrder?.orderItems || []).forEach((eachOrderItem: any) => {
        const addonId = Number(eachOrderItem?.serviceAddons?.id);
        const quantity = Number(eachOrderItem?.quantity || 0);

        if (addonId === 1) acc.bartender += quantity;
        if (addonId === 2) acc.waiter += quantity;
      });

      return acc;
    },
    { bartender: 0, waiter: 0 },
  );

  const bartenderCount =
    record?.bartender !== undefined && record?.bartender !== null
      ? Number(record?.bartender)
      : serviceAddonCounts.bartender;

  const waiterCount =
    record?.waiter !== undefined && record?.waiter !== null
      ? Number(record?.waiter)
      : serviceAddonCounts.waiter;

  const isBookByDish = record?.bookingType === ChefForPartyBookingTypeEnum.BOOK_BY_DISH;
  // Party response includes soft-deleted dish rows (paranoid: false) for admins.
  const bbdDishes = Array.isArray(record?.dishes)
    ? record.dishes.filter((d: any) => !d.deletedAt)
    : [];
  const bbdTotalTime = Number(record?.cookingTime ?? 0);
  // BBD only: serving time = visit time + cooking time
  const bbdServingTime = (() => {
    if (!record?.time) return "-";
    const parsed = moment(record.time, ["hh:mm A", "h:mm A", "HH:mm", "hh:mm a", "h:mm a", "HH:mm:ss"]);
    if (!parsed.isValid()) return record.time;
    return parsed.add(bbdTotalTime, "minutes").format("h:mm A");
  })();
  // Show minutes for sub-hour durations, and minutes + hours otherwise
  // (e.g. 45 -> "45 min", 90 -> "90 min (1.5 hrs)").
  const bbdCookingTimeLabel = (() => {
    const mins = Number(bbdTotalTime) || 0;
    if (mins < 60) return `${mins} min`;
    const hrs = parseFloat((mins / 60).toFixed(2));
    return `${mins} min (${hrs} ${hrs === 1 ? "hr" : "hrs"})`;
  })();
  const bbdDishesCost = bbdDishes.reduce((sum: number, d: any) => sum + (Number(d.price) || 0), 0);

  return (
    <WorkspacePanel>
      <WorkspaceSection>
        <SectionHeader>
          <WorkSpaceTitleRow>
            <ChefCapIcon color={colors.primary} height={24} width={24} />
            <SectionTitle>{isBookByDish ? "Book by Dish" : "Party Overview"}</SectionTitle>
          </WorkSpaceTitleRow>
        </SectionHeader>
      </WorkspaceSection>
      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>#</ShowLabel>
          <ShowLabel style={{ marginBottom: "0px" }}>ID:</ShowLabel>
          <ShowValue>{record?.id}</ShowValue>
          <Tooltip title="Copy ID">
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(String(record?.id))}
              sx={{ p: 0.25 }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </HorizontalContainer>
        <HorizontalContainer>
          <UserIcon color={"#6C7C93"} width={18} height={18} />
          <ShowLabel style={{ marginBottom: "0px" }}>Customer:</ShowLabel>
          <ShowValue>{record?.customerName}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <CustomCallIconButton
            color="#6C7C93"
            size={18}
            toPhoneNumber={record?.customerPhone}
            userType={UserType.CUSTOMER}
            referenceId={Number(record?.id)}
            referenceModelName="CHEF_FOR_PARTY"
          />
          <ShowLabel style={{ marginBottom: "0px" }}>Phone:</ShowLabel>
          <ShowValue>{record?.customerPhone}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      <ThreeColGrid>
        <HorizontalContainer>
          <CalendarIcon color={"#6C7C93"} width={16} height={16} />
          <ShowLabel style={{ marginBottom: "0px" }}>Date:</ShowLabel>
          <ShowValue>{formatDate(record?.date)}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ClockIcon color={"#6C7C93"} width={16} height={16} />
          <ShowLabel style={{ marginBottom: "0px" }}>{isBookByDish ? "Visit Time:" : "Serving Time:"}</ShowLabel>
          <ShowValue>{record?.time || "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Status:</ShowLabel>
          <ShowValue>{record?.status || "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      {isBookByDish && (
        <>
          <HorizontalLine />
          <ThreeColGrid>
            <HorizontalContainer>
              <ClockIcon color={"#6C7C93"} width={16} height={16} />
              <ShowLabel style={{ marginBottom: "0px" }}>Cooking Time:</ShowLabel>
              <ShowValue>{bbdCookingTimeLabel}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer>
              <ClockIcon color={"#6C7C93"} width={16} height={16} />
              <ShowLabel style={{ marginBottom: "0px" }}>Serving Time:</ShowLabel>
              <ShowValue>{bbdServingTime}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Dishes Cost:</ShowLabel>
              <ShowValue>₹{bbdDishesCost}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Visit Charges:</ShowLabel>
              <ShowValue>₹{Number(record?.visitCharges ?? 0)}</ShowValue>
            </HorizontalContainer>
            <HorizontalContainer>
              <ShowLabel style={{ marginBottom: "0px" }}>Surge Price:</ShowLabel>
              <ShowValue>₹{Number(record?.surgePrice ?? 0)}</ShowValue>
            </HorizontalContainer>
          </ThreeColGrid>
        </>
      )}

      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Gender Pref:</ShowLabel>
          <ShowValue>
            {record?.genderPreferences ? record?.genderPreferences : "Any"}
          </ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Occasion:</ShowLabel>
          <ShowValue>{record?.occasion || "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>OTP:</ShowLabel>
          <ShowValue>{record?.otp || "-"}</ShowValue>
        </HorizontalContainer>
      </ThreeColGrid>

      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Veg People:</ShowLabel>
          <ShowValue>{record?.vegOnlyPeople || "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Burners:</ShowLabel>
          <ShowValue>{record?.numberOfBurners || "-"}</ShowValue>
        </HorizontalContainer>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>People Count By Cook:</ShowLabel>
          <ShowValue>{record?.peopleCountByCook ?? "-"}</ShowValue>
        </HorizontalContainer>

        {record?.rescheduledFrom && (
          <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Rescheduled From:</ShowLabel>
          <Link href={`/#/ChefForParty/${record?.rescheduledFrom}/show`}>{record?.rescheduledFrom || "-"}</Link>
        </HorizontalContainer>
          )}
      </ThreeColGrid>

      <HorizontalLine />

        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Food Preferences:</ShowLabel>
          <ShowValue>{foodPreferences}</ShowValue>
        </HorizontalContainer>

      <HorizontalLine />

      <ThreeColGrid>
        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>Cash Collected:</ShowLabel>
          <StatusBooleanBadge active={record?.isCashCollected}>
            {record?.isCashCollected ? "Yes" : "No"}
          </StatusBooleanBadge>
        </HorizontalContainer>

        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>
            No Onion/Garlic:
          </ShowLabel>
          <StatusBooleanBadge active={record?.nonOnionGarlic}>
            {record?.nonOnionGarlic ? "Yes" : "No"}
          </StatusBooleanBadge>
        </HorizontalContainer>

        <HorizontalContainer>
          <ShowLabel style={{ marginBottom: "0px" }}>
            Allowed Late Payment:
          </ShowLabel>
          <StatusBooleanBadge active={record?.allowLatePayment}>
            {record?.allowLatePayment ? "Yes" : "No"}
          </StatusBooleanBadge>
        </HorizontalContainer>
      </ThreeColGrid>

      {!isBookByDish && (
        <>
          <HorizontalLine />

          <div style={{display: 'flex', flexWrap: 'wrap', gap: '16px'}}>

            <InfoPill>
              <UsersIcon color={colors.primaryDark} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Adults
                </ShowLabel>
                <HighlightValue>{record?.noOfAdults || "0"}</HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(250, 245, 255)", border: '1px solid rgb(126, 34, 206, 0.1)' }}>
              <UsersIcon color={"rgb(126, 34, 206)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Children
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(126, 34, 206)" }}>
                  {record?.noOfChildren || "0"}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(255, 251, 235)", border: '1px solid rgb(180, 83, 9, 0.1)' }}>
              <GlassIcon color={"rgb(180, 83, 9)"} width={16} height={16} />
                <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Bartender
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(180, 83, 9)" }}>
                  {bartenderCount}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(240, 253, 250)", border: '1px solid rgb(15, 118, 110, 0.1)' }}>
              <ClipboardIcon color={"rgb(15, 118, 110)"} width={16} height={16} />
                <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Waiter
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(15, 118, 110)" }}>
                  {waiterCount}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(255, 247, 237)", border: '1px solid rgb(194, 65, 12, 0.1)' }}>
              <ForkSpoonIcon color={"rgb(194, 65, 12)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Starters
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(194, 65, 12)" }}>
                  {record?.noOfStarters || "0"}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(236, 253, 245)", border: '1px solid rgb(4, 120, 87, 0.1)' }}>
              <ForkSpoonIcon color={"rgb(4, 120, 87)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Main Course
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(4, 120, 87)" }}>
                  {record?.noOfMainCourse || "0"}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(253, 242, 248)", border: '1px solid rgb(190, 24, 93, 0.1)' }}>
              <ForkSpoonIcon color={"rgb(190, 24, 93)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Desserts
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(190, 24, 93)" }}>
                  {record?.noOfDesserts || "0"}
                </HighlightValue>

            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(249, 250, 251)", border: '1px solid rgb(55, 65, 81, 0.1)' }}>
              <ForkSpoonIcon color={"rgb(55, 65, 81)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Side Dishes
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(55, 65, 81)" }}>
                  {record?.noOfSideDishes || "0"}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgb(249, 250, 251)", border: '1px solid rgb(55, 65, 81, 0.1)' }}>
              <ForkSpoonIcon color={"rgb(55, 65, 81)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Beverages
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(55, 65, 81)" }}>
                  {record?.noOfBeverages || "0"}
                </HighlightValue>
            </InfoPill>

            <InfoPill style={{ backgroundColor: "rgba(25, 117, 210, 0.1)", border: '1px solid rgb(25, 117, 210, 0.1)' }}>
              <PackageIcon color={"rgb(25, 117, 210)"} width={16} height={16} />
              <ShowLabel style={{ fontSize: "16px", fontWeight: "normal", marginBottom: '0' }}>
                  Total Dishes
                </ShowLabel>
                <HighlightValue style={{ color: "rgb(25, 117, 210)" }}>
                  {record?.noOfDishes || "0"}
                </HighlightValue>
            </InfoPill>
          </div>
        </>
      )}

      {record?.customerCookRating && (
        <>
          <HorizontalLine />
          <HorizontalContainer>
            <ShowLabel style={{ marginBottom: "0px" }}>Cook Customer Rating:</ShowLabel>
            <span style={{ fontWeight: 700, fontSize: "15px", color: colors.primary }}>
              {record.customerCookRating.overallRating ?? "-"}/5
            </span>
            {record.customerCookRating.ratingTags?.comments && (
              <>
                <ShowLabel style={{ marginBottom: "0px" }}>Comments:</ShowLabel>
                <ShowValue>
                  {Array.isArray(record.customerCookRating.ratingTags.comments)
                    ? record.customerCookRating.ratingTags.comments.join(", ")
                    : Object.values(record.customerCookRating.ratingTags.comments as Record<string, string>).join(", ")}
                </ShowValue>
              </>
            )}
          </HorizontalContainer>
        </>
      )}
    </WorkspacePanel>
  );
};

export default ChefForPartyOverview;
